import { Grid } from "@material-ui/core";
import { noop } from "lodash";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Controller, FormProvider } from "react-hook-form";
import styled from "styled-components";
import { Button } from "../../../../components/button";
import { Country } from "../../../../components/forms/country";
import { DateField } from "../../../../components/forms/date";
import { TextField } from "../../../../components/forms/text";
import { KycIcon } from "../../../../components/icons/status";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import { Permission } from "../../../../components/permission";
import { Tabs } from "../../../../components/tabs/tabs-page";
import { Widget } from "../../../../components/widget";
import { countries } from "../../../../store/common/models";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { VerificationService } from "../../../../store/verification/service";
import { CanEditKyc } from "./can-edit-kyc";
import ChecksPerformed from "./checks-performed";
import {
  Label,
  StyledButton,
  StyledCheckbox,
  StyledWidget,
} from "./components";
import { SingleKycContext } from "./context";
import KycHistory from "./kyc-history";
import SaveDeclinedReasons from "./save-declined-reasons";
import { StatusChangeModal } from "./status-change-modal";
import { useKycUpdate } from "./update-hook";

const CallbackButton = styled(Button)`
  & + & {
    margin-left: 1em;
  }
`;

export const SingleKyc: FunctionComponent<{
  fraudCheckId: string;
  item: VerificationDTO.Responses.GetSingleKyc.T;
}> = ({ item, fraudCheckId }) => {
  const {
    user: {
      dateOfBirth,
      emailAddress,
      firstName,
      lastName,
      middleName,
      locale,
      nationality,
    },
    userStatus,
    ongoingBackgroundChecks,
    createdDate,
    serviceDetails,
  } = item;

  const { isEditing, setIsEditing, form, onSubmit, ...updates } = useKycUpdate(
    item
  );

  const { setOverlay } = useContext(GlobalOverlayContext);

  const { setItem } = useContext(SingleKycContext);

  useEffect(() => {
    setItem(item);
  }, [item]);

  const allServices = useMemo(() => serviceDetails.map((x) => x.service), [
    serviceDetails,
  ]);

  const onOverlay = useCallback(
    () =>
      setOverlay({
        title: "Update KYC Status",
        children: <StatusChangeModal item={item} allServices={allServices} />,
      }),
    [item, allServices]
  );

  const tabs = useMemo(
    () => [
      {
        title: "Checks Performed",
        content: (
          <Widget title="Checks Performed">
            <ChecksPerformed
              {...updates}
              fraudCheckId={fraudCheckId}
              item={item}
              isEditing={isEditing}
            />
          </Widget>
        ),
      },
      {
        title: "KYC History",
        content: (
          <Widget title="KYC History">
            <KycHistory fraudCheckId={fraudCheckId} />
          </Widget>
        ),
      },
    ],
    [
      updates.addDocType,
      updates.idDocType,
      updates.setAddDocType,
      updates.setIdDocType,
      fraudCheckId,
      item,
      isEditing,
    ]
  );

  return (
    <form onSubmit={onSubmit}>
      <StyledWidget
        title="Overview"
        adornment={
          <CanEditKyc>
            {isEditing ? (
              <>
                <StyledButton
                  colour="danger"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  Cancel
                </StyledButton>
                <StyledButton colour="success" type="submit">
                  Save
                </StyledButton>
              </>
            ) : (
              <StyledButton onClick={() => setIsEditing(true)}>
                Edit KYC Details
              </StyledButton>
            )}
            <StyledButton colour="primary" onClick={onOverlay}>
              Update KYC Status
            </StyledButton>
            <SaveDeclinedReasons />
          </CanEditKyc>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>Name:</Label>
              </Grid>
              <Grid item xs={isEditing ? 6 : 3}>
                {isEditing ? (
                  <>
                    <Controller
                      control={form.control}
                      name="userDetails.firstName"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message}
                          fullWidth
                          type="text"
                          label="First Name"
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="userDetails.middleName"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message}
                          fullWidth
                          type="text"
                          label="Middle Name"
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="userDetails.lastName"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error?.message}
                          fullWidth
                          type="text"
                          label="Last Name"
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    {firstName} {middleName} {lastName}
                  </>
                )}
              </Grid>
              <Grid item xs={6}>
                <Label>Email:</Label>
              </Grid>
              <Grid item xs={6}>
                {emailAddress}
              </Grid>
              <Grid item xs={6}>
                <Label>Language:</Label>
              </Grid>
              <Grid item xs={6}>
                {locale}
              </Grid>
              <Grid item xs={6}>
                <Label>Date of Birth:</Label>
              </Grid>
              <Grid item xs={6}>
                {isEditing ? (
                  <Controller
                    control={form.control}
                    name="userDetails.dateOfBirth"
                    render={({ field, fieldState: { error } }) => (
                      <DateField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        type="text"
                        label="Date of Birth"
                      />
                    )}
                  />
                ) : (
                  dateOfBirth?.local().format("yyyy-MM-DD")
                )}
              </Grid>
              <Grid item xs={6}>
                <Label>Nationality:</Label>
              </Grid>
              <Grid item xs={6}>
                {isEditing ? (
                  <Controller
                    control={form.control}
                    name="userDetails.nationality"
                    render={({ field, fieldState: { error } }) => (
                      <Country
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        type="text"
                        label="Nationality"
                      />
                    )}
                  />
                ) : nationality ? (
                  countries.enum[nationality]
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>Status:</Label>
              </Grid>
              <Grid item xs={6}>
                {KycIcon(userStatus, "kyc")}
              </Grid>
              <Grid item xs={6}>
                <Label>Date Submitted:</Label>
              </Grid>
              <Grid item xs={6}>
                {createdDate.local().format("yyyy-MM-DD HH:mm")}
              </Grid>
              <Grid item xs={6}>
                <Label>Has ongoing background checks:</Label>
              </Grid>
              <Grid item xs={6}>
                <StyledCheckbox checked={ongoingBackgroundChecks} disabled />
              </Grid>
              <Permission roles={["ROLE_ADMIN", "ROLE_MERCHANT"]}>
                <Grid item xs={12}>
                  <CallbackButton
                    colour="warning"
                    onClick={() =>
                      VerificationService.resendCallback(fraudCheckId).catch(
                        noop
                      )
                    }
                  >
                    Resend Callback
                  </CallbackButton>

                  <CallbackButton
                    colour="secondary"
                    onClick={() =>
                      VerificationService.downloadKYCReport(fraudCheckId)
                    }
                  >
                    Download Report
                  </CallbackButton>
                </Grid>
              </Permission>
            </Grid>
          </Grid>
        </Grid>
      </StyledWidget>
      <FormProvider {...form}>
        <Tabs tabs={tabs} />
      </FormProvider>
    </form>
  );
};
